import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmt$ } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

const CONDITIONS = [
  { key: "Light", desc: "Routine cleaning" },
  { key: "Moderate", desc: "Noticeably dirty" },
  { key: "Heavy", desc: "Needs extra attention" },
  { key: "Extreme", desc: "Significant dirt/stains/pet hair" },
];
const SIZES = ["Sedan", "SUV", "Truck"];

export default function RequestDetail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service_id: params.get("service") || "",
    vehicle_size: params.get("size") || "Sedan",
    addon_ids: [],
    condition: "Light",
    vehicle_year: "", vehicle_make: "", vehicle_model: "",
    customer_name: "", customer_email: "", customer_phone: "",
    address: "", preferred_date: "", preferred_time: "10:00", notes: "",
    referral_code: (params.get("ref") || "").toUpperCase(),
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/addons").then((r) => setAddons(r.data));
  }, []);

  const selectedService = useMemo(() => services.find((s) => s.id === form.service_id), [services, form.service_id]);
  const sizePrice = useMemo(() => {
    if (!selectedService) return null;
    const s = selectedService.sizes.find((x) => x.size === form.vehicle_size);
    if (!s) return null;
    const promoActive = selectedService.promo_active && s.promo_price;
    return { regular: s.regular_price, promo: s.promo_price, effective: promoActive ? s.promo_price : s.regular_price, promoActive };
  }, [selectedService, form.vehicle_size]);
  const selectedAddons = addons.filter((a) => form.addon_ids.includes(a.id));
  const addonTotal = selectedAddons.reduce((s, a) => s + a.price, 0);
  const subtotal = (sizePrice?.effective || 0) + addonTotal;
  const depositPct = selectedService?.deposit_percent || 10;
  const deposit = subtotal * depositPct / 100;

  const toggleAddon = (id) => {
    setForm((f) => ({ ...f, addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter(x => x !== id) : [...f.addon_ids, id] }));
  };

  const canNext = () => {
    if (step === 1) return !!form.service_id;
    if (step === 2) return !!form.vehicle_size && !!form.condition;
    if (step === 3) return form.customer_name && form.customer_email && form.customer_phone && form.address && form.preferred_date && form.preferred_time;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/booking-requests", form);
      toast.success("Request submitted! Check your email.");
      nav(`/booking/${data.magic_token}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  const Step = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
        ${step >= n ? "bg-[#CCFF00] text-black" : "bg-zinc-800 text-zinc-500"}`}>{n}</div>
      <div className={`text-xs uppercase tracking-widest ${step === n ? "text-[#CCFF00]" : "text-zinc-500"}`}>{label}</div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="text-xs uppercase tracking-widest text-[#CCFF00]">Request a Detail</div>
      <h1 className="font-display text-4xl mt-2 mb-6">Book your service</h1>

      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <Step n={1} label="Service" />
        <div className="h-px flex-1 bg-zinc-800" />
        <Step n={2} label="Vehicle" />
        <div className="h-px flex-1 bg-zinc-800" />
        <Step n={3} label="Details" />
        <div className="h-px flex-1 bg-zinc-800" />
        <Step n={4} label="Review" />
      </div>

      {step === 1 && (
        <div className="space-y-4">
          {services.map((s) => {
            const priceRow = s.sizes.find(x => x.size === form.vehicle_size) || s.sizes[0];
            const promo = s.promo_active && priceRow.promo_price;
            const selected = form.service_id === s.id;
            return (
              <div key={s.id}
                data-testid={`select-service-${s.id}`}
                onClick={() => setForm({ ...form, service_id: s.id })}
                className={`card-surface p-5 cursor-pointer border-2 transition ${selected ? "border-[#CCFF00]" : "border-transparent hover:border-[#CCFF00]/40"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-2xl">{s.name}</div>
                    <div className="text-zinc-400 text-sm mt-1">{s.description}</div>
                    <div className="text-xs text-zinc-500 mt-2">{s.duration_minutes} min · {s.deposit_percent}% deposit</div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    {promo ? (
                      <>
                        <div className="text-[#CCFF00] font-display text-2xl">{fmt$(priceRow.promo_price)}</div>
                        <div className="line-through text-zinc-500 text-sm">{fmt$(priceRow.regular_price)}</div>
                      </>
                    ) : (
                      <div className="font-display text-2xl">{fmt$(priceRow.regular_price)}</div>
                    )}
                  </div>
                </div>
                {selected && <CheckCircle2 className="text-[#CCFF00] mt-3" size={20} />}
              </div>
            );
          })}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Vehicle Size</div>
            <div className="grid grid-cols-3 gap-3">
              {SIZES.map((sz) => (
                <button key={sz} data-testid={`vehicle-size-${sz.toLowerCase()}`}
                  onClick={() => setForm({ ...form, vehicle_size: sz })}
                  className={`p-4 rounded-xl border-2 uppercase tracking-widest text-sm font-semibold transition
                    ${form.vehicle_size === sz ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                  {sz}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["Year", "Make", "Model"].map((lbl, i) => (
              <input key={lbl} data-testid={`vehicle-${lbl.toLowerCase()}`}
                placeholder={lbl}
                value={form[`vehicle_${lbl.toLowerCase()}`]}
                onChange={(e) => setForm({ ...form, [`vehicle_${lbl.toLowerCase()}`]: e.target.value })}
                className="px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Current condition</div>
            <div className="grid grid-cols-2 gap-3">
              {CONDITIONS.map((c) => (
                <button key={c.key} data-testid={`condition-${c.key.toLowerCase()}`}
                  onClick={() => setForm({ ...form, condition: c.key })}
                  className={`p-4 rounded-xl border-2 text-left transition
                    ${form.condition === c.key ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-zinc-700 hover:border-zinc-500"}`}>
                  <div className="font-semibold">{c.key}</div>
                  <div className="text-xs text-zinc-400">{c.desc}</div>
                </button>
              ))}
            </div>
            {(form.condition === "Heavy" || form.condition === "Extreme") && (
              <div className="mt-3 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                Heavy/Extreme conditions are flagged for admin review - your final price may be adjusted.
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Add-ons (optional)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {addons.map((a) => (
                <label key={a.id} data-testid={`addon-${a.id}`}
                  className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer
                    ${form.addon_ids.includes(a.id) ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-zinc-700"}`}>
                  <div>
                    <div className="font-semibold text-sm">{a.name}</div>
                    <div className="text-xs text-zinc-400">+{fmt$(a.price)}</div>
                  </div>
                  <input type="checkbox" checked={form.addon_ids.includes(a.id)} onChange={() => toggleAddon(a.id)} />
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {[
            ["customer_name", "Full Name"], ["customer_email", "Email"], ["customer_phone", "Mobile phone"],
            ["address", "Service Address"],
          ].map(([k, lbl]) => (
            <input key={k} data-testid={`field-${k}`}
              placeholder={lbl} value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
          ))}
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="field-preferred-date" type="date"
              value={form.preferred_date}
              onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
              className="px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
            <input data-testid="field-preferred-time" type="time"
              value={form.preferred_time}
              onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
              className="px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
          </div>
          <textarea data-testid="field-notes" placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none min-h-[80px]" />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="card-surface p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Service</div>
            <div className="font-display text-xl">{selectedService?.name}</div>
            <div className="text-sm text-zinc-400">{form.vehicle_size} · {form.condition}</div>
            <div className="mt-3 flex justify-between text-sm">
              <span>Service</span><span>{fmt$(sizePrice?.effective)}</span>
            </div>
            {selectedAddons.map((a) => (
              <div key={a.id} className="flex justify-between text-sm text-zinc-400">
                <span>{a.name}</span><span>+{fmt$(a.price)}</span>
              </div>
            ))}
            {form.referral_code && (
              <div className="flex justify-between text-sm text-[#CCFF00] mt-2">
                <span>Referral code applied</span><span className="font-mono">{form.referral_code}</span>
              </div>
            )}
            <div className="border-t border-zinc-800 mt-3 pt-3 flex justify-between font-semibold">
              <span>Starting total</span><span>{fmt$(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#CCFF00] mt-1 text-sm">
              <span>Deposit due after approval ({depositPct}%)</span><span>{fmt$(deposit)}</span>
            </div>
          </div>
          <input data-testid="field-referral-code" placeholder="Referral code (optional)"
            value={form.referral_code}
            onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
            className="w-full px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none font-mono" />
          <div className="text-xs text-zinc-400">
            This is a request. Our admin will review, assign a detailer and either accept your time or propose an alternate. You'll be notified by email.
          </div>
          <button data-testid="submit-request-btn" disabled={submitting} onClick={submit}
            className="neon-btn w-full py-4 rounded-xl">
            {submitting ? "Submitting..." : "Submit request"}
          </button>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button data-testid="step-back-btn" disabled={step === 1} onClick={() => setStep(step - 1)}
          className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white disabled:opacity-30 flex items-center gap-2">
          <ArrowLeft size={16} /> Back
        </button>
        {step < 4 && (
          <button data-testid="step-next-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}
            className="neon-btn px-6 py-3 rounded-xl flex items-center gap-2">
            Next <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
